import {
    Button,
    Col,
    Form,
    Input,
    Row,
    Select,
    Spin,
    Typography,
    message,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd/lib/form';
import { ReactElement, useEffect, useRef, useState } from 'react';
import CcxComponentProps from '../../../core/CcxComponent';
import InfoIcon from '@severalnines/bar-frontend-components/build/lib/General/InfoIcon';
import ProvisionService from '../../../services/ProvisionService';
import {
    CustomValuePolicy,
    CustomValueRow,
} from '../../../types/CustomValuePolicy';
import { showMetadata } from '../../../core/CcxEnv';

interface Props extends CcxComponentProps {
    form?: FormInstance;
    // When provided, skips the network fetch and uses these policies directly.
    // Read once on mount. Used by the debug harness and unit tests.
    policiesOverride?: CustomValuePolicy[];
    // Overrides the FE_SHOW_METADATA feature flag. Used by the debug harness
    // and unit tests to exercise both visibility paths.
    showMetadataOverride?: boolean;
}

export const MAX_CUSTOM_VALUES = 32;
export const CUSTOM_VALUES_FIELD = 'customValues';

// The metadata policy does not change while the wizard is open, so it is
// fetched once and reused when the user navigates back to this step (the
// component remounts, but the parent form — and the user's edits — persist).
let cachedPolicies: CustomValuePolicy[] | undefined;

// Exposed for tests to reset the module-level cache between cases.
export function __resetPolicyCache() {
    cachedPolicies = undefined;
}

/**
 * Resolves the default value to pre-fill for a policy row. For select policies
 * the default is only used when it matches one of the available options.
 */
function defaultValueFor(policy: CustomValuePolicy): string {
    if (policy.default === undefined || policy.default === null) return '';
    if (policy.type === 'select') {
        const isValidOption = policy.options?.some(
            (option) => option.id === policy.default
        );
        return isValidOption ? policy.default : '';
    }
    return policy.default;
}

/**
 * Resolves the display label for a select value (the option's `name`, falling
 * back to its `id`). Unknown ids are returned as-is.
 */
function labelForOption(policy: CustomValuePolicy, valueId: string): string {
    const option = policy.options?.find((o) => o.id === valueId);
    return option ? option.name || option.id : valueId;
}

export function CustomMetadata({
    testId = 'CustomMetadata',
    form,
    policiesOverride,
    showMetadataOverride,
}: Props): ReactElement {
    const { Text } = Typography;
    // Seed from the policy already fetched in this wizard session so a remount
    // renders its final state on the first paint — without it the section would
    // briefly appear before being hidden again when the policy turns out empty.
    const initialPolicies = policiesOverride ?? cachedPolicies;
    const [policies, setPolicies] = useState<CustomValuePolicy[]>(
        initialPolicies ?? []
    );
    const [loaded, setLoaded] = useState<boolean>(!!initialPolicies);
    const seededRef = useRef<boolean>(false);

    useEffect(() => {
        if (loaded) return;
        fetchConfig();
    }, []);

    const fetchConfig = async () => {
        try {
            const response = await ProvisionService.getConfig();
            cachedPolicies = response?.custom_values ?? [];
            setPolicies(cachedPolicies);
        } catch (e) {
            // Degrade gracefully: without a policy, users can still add their
            // own custom values, so the wizard must not be blocked. Not cached,
            // so a later visit retries the fetch.
            console.error(e);
            setPolicies([]);
        } finally {
            setLoaded(true);
        }
    };

    // Seed the form only once the policies are known — never before — so each
    // policy row renders with its read-only, labelled key from the first paint
    // instead of briefly flashing the raw id in an editable input. Any
    // user-added rows already present are preserved and kept last.
    useEffect(() => {
        if (!loaded || seededRef.current || !form) return;
        seededRef.current = true;
        const existing = form.getFieldValue(CUSTOM_VALUES_FIELD) || [];
        const policyRows = policies.map((policy) => {
            const prior = existing.find((row: any) => row?.key === policy.id);
            // Keep a value the user already picked on a previous visit; only
            // fall back to the policy default when seeding fresh.
            const value = prior ? prior.value : defaultValueFor(policy);
            const row: CustomValueRow = {
                key: policy.id,
                value,
                keyLabel: policy.name || policy.id,
            };
            if (policy.type === 'select') {
                row.valueLabel = labelForOption(policy, value);
            }
            return row;
        });
        const userRows = existing.filter(
            (row: any) => !policies.some((policy) => policy.id === row?.key)
        );
        form.setFieldsValue({
            [CUSTOM_VALUES_FIELD]: [...policyRows, ...userRows],
        });
    }, [loaded, policies, form]);

    const isBlank = (value: unknown) =>
        value === undefined || value === null || String(value).trim() === '';

    const duplicateKeyValidator = (currentName: number) => ({
        validator(_: any, value: string) {
            const trimmed = String(value ?? '').trim();
            if (!trimmed) return Promise.resolve();
            const rows = form?.getFieldValue(CUSTOM_VALUES_FIELD) || [];
            const hasDuplicate = rows.some(
                (row: any, index: number) =>
                    index !== currentName &&
                    String(row?.key ?? '').trim() === trimmed
            );
            if (hasDuplicate) {
                // Name the key: the row it collides with may be a policy row,
                // whose input shows the policy label rather than the key.
                return Promise.reject(
                    new Error(`Key "${trimmed}" is already used`)
                );
            }
            return Promise.resolve();
        },
    });

    // A duplicate involves two rows, but antd only re-runs the rules of the
    // field being edited. Without re-running the siblings, the error would show
    // on just one of the pair and would linger on the row that was left alone
    // after the *other* one is renamed or removed. Blank keys are skipped: they
    // can never be duplicates, and validating them would flag rows the user has
    // not filled in yet as "Key is required".
    const revalidateKeys = (exceptName?: number) => {
        const rows = form?.getFieldValue(CUSTOM_VALUES_FIELD) || [];
        const paths = rows
            .map((row: any, index: number) => ({ row, index }))
            .filter(
                ({ row, index }: { row: any; index: number }) =>
                    index !== exceptName && !isBlank(row?.key)
            )
            .map(({ index }: { index: number }) => [
                CUSTOM_VALUES_FIELD,
                index,
                'key',
            ]);
        if (paths.length === 0) return;
        // Rejects when a duplicate is found — that is the point, the message is
        // rendered by the Form.Item — so the rejection itself is not an error.
        form?.validateFields(paths).catch(() => {});
    };

    // Removing a row shifts the indices the remaining rows' rules are bound to,
    // so their revalidation has to wait for the re-render that follows.
    const [pendingKeyRevalidation, setPendingKeyRevalidation] =
        useState<number>(0);
    useEffect(() => {
        if (pendingKeyRevalidation === 0) return;
        revalidateKeys();
    }, [pendingKeyRevalidation]);

    // List-level validation. The wizard gates each step with
    // form.validateFields(['customValues']); antd v4 does not recurse into
    // nested Form.List item rules from a parent path, so the constraints that
    // must block progression (required policy values, unique keys, orphan
    // values, the 32 cap) are enforced here where they are reliably run.
    const customValuesValidator = async (_: any, rows: any[]) => {
        // Block progression until the policy has loaded. The list is always
        // mounted (so this validator is registered), but required policy fields
        // are unknown until the fetch resolves — failing here prevents a fast
        // click from advancing the step before those constraints exist.
        if (!loaded) {
            throw new Error('Loading metadata policy, please wait.');
        }
        const list = rows || [];

        const missingRequired = policies
            .filter((policy) => policy.required)
            .filter((policy) => {
                const row = list.find((r) => r?.key === policy.id);
                return !row || isBlank(row.value);
            })
            .map((policy) => policy.name || policy.id);
        if (missingRequired.length > 0) {
            throw new Error(
                `Please provide a value for: ${missingRequired.join(', ')}`
            );
        }

        const orphanValue = list.some(
            (row) => !isBlank(row?.value) && isBlank(row?.key)
        );
        if (orphanValue) {
            throw new Error('Every custom value needs a key.');
        }

        const keys = list
            .map((row) => String(row?.key ?? '').trim())
            .filter(Boolean);
        const duplicateKeys = Array.from(
            new Set(keys.filter((key, index) => keys.indexOf(key) !== index))
        );
        if (duplicateKeys.length > 0) {
            throw new Error(`Keys must be unique: ${duplicateKeys.join(', ')}`);
        }

        if (list.length > MAX_CUSTOM_VALUES) {
            throw new Error(
                `You may add up to ${MAX_CUSTOM_VALUES} custom values`
            );
        }
    };

    const hasRequiredPolicy = policies.some((policy) => policy.required);

    // The section is only offered when the operator opted in through the
    // FE_SHOW_METADATA flag, or when their policy defines at least one key.
    //
    // While the policy is still loading the outcome is only known when the flag
    // forces the section on — so that is the one case where the heading and the
    // spinner are shown. Without the flag nothing is rendered until the policy
    // resolves: showing a heading and a spinner that then vanish, because the
    // policy turned out to be empty, reads as a glitch. The Form.List itself
    // stays mounted throughout either way, so its validator keeps gating the
    // step while loading (see customValuesValidator).
    const flagEnabled = showMetadataOverride ?? showMetadata;
    const visible = flagEnabled || (loaded && policies.length > 0);

    return (
        <div data-testid={testId}>
            {visible && (
                <p>
                    <strong>
                        Metadata{' '}
                        {/* Withheld until the policy is known, so the heading
                            does not switch from "(optional)" to required. */}
                        {loaded && !hasRequiredPolicy && (
                            <Text type="secondary">(optional)</Text>
                        )}
                    </strong>
                    <InfoIcon
                        info={
                            <span>
                                Assign custom key/value tags to your datastore.
                                Keys defined by your operator's policy are
                                read-only. You may add up to {MAX_CUSTOM_VALUES}{' '}
                                values.
                            </span>
                        }
                    />
                </p>
            )}
            <Form.List
                name={CUSTOM_VALUES_FIELD}
                rules={[{ validator: customValuesValidator }]}
            >
                {(fields, { add, remove }) =>
                    !visible ? null : !loaded ? (
                        <Spin size="small" />
                    ) : (
                        <>
                            {fields.map((field) => {
                                const rowKey = form?.getFieldValue([
                                    CUSTOM_VALUES_FIELD,
                                    field.name,
                                    'key',
                                ]);
                                const policy = policies.find(
                                    (p) => p.id === rowKey
                                );
                                const isPolicyRow = !!policy;
                                return (
                                    <Row gutter={8} key={field.key} align="top">
                                        <Col span={10}>
                                            {isPolicyRow ? (
                                                <Form.Item
                                                    label={
                                                        field.name === 0
                                                            ? 'Key'
                                                            : undefined
                                                    }
                                                >
                                                    <Input
                                                        data-testid={`${testId}Key${field.name}`}
                                                        value={
                                                            policy?.name ||
                                                            policy?.id
                                                        }
                                                        disabled={true}
                                                    />
                                                </Form.Item>
                                            ) : (
                                                <Form.Item
                                                    label={
                                                        field.name === 0
                                                            ? 'Key'
                                                            : undefined
                                                    }
                                                    name={[field.name, 'key']}
                                                    rules={[
                                                        {
                                                            required: true,
                                                            message:
                                                                'Key is required',
                                                        },
                                                        duplicateKeyValidator(
                                                            field.name
                                                        ),
                                                    ]}
                                                >
                                                    <Input
                                                        data-testid={`${testId}Key${field.name}`}
                                                        placeholder="Key"
                                                        maxLength={200}
                                                        onChange={() =>
                                                            revalidateKeys(
                                                                field.name
                                                            )
                                                        }
                                                    />
                                                </Form.Item>
                                            )}
                                        </Col>
                                        <Col span={10}>
                                            <Form.Item
                                                label={
                                                    field.name === 0
                                                        ? 'Value'
                                                        : undefined
                                                }
                                                name={[field.name, 'value']}
                                                rules={
                                                    policy?.required
                                                        ? [
                                                              {
                                                                  required:
                                                                      true,
                                                                  message: `${
                                                                      policy.name ||
                                                                      policy.id
                                                                  } is required`,
                                                              },
                                                          ]
                                                        : []
                                                }
                                            >
                                                {policy?.type === 'select' ? (
                                                    <Select
                                                        data-testid={`${testId}Value${field.name}`}
                                                        placeholder="Select a value"
                                                        onChange={(val) =>
                                                            form?.setFieldValue(
                                                                [
                                                                    CUSTOM_VALUES_FIELD,
                                                                    field.name,
                                                                    'valueLabel',
                                                                ],
                                                                labelForOption(
                                                                    policy,
                                                                    val as string
                                                                )
                                                            )
                                                        }
                                                        options={policy.options?.map(
                                                            (option) => ({
                                                                value: option.id,
                                                                label:
                                                                    option.name ||
                                                                    option.id,
                                                            })
                                                        )}
                                                    />
                                                ) : (
                                                    <Input
                                                        data-testid={`${testId}Value${field.name}`}
                                                        placeholder="Value"
                                                        maxLength={200}
                                                    />
                                                )}
                                            </Form.Item>
                                        </Col>
                                        <Col span={4}>
                                            {!isPolicyRow && (
                                                <Form.Item
                                                    label={
                                                        field.name === 0
                                                            ? ' '
                                                            : undefined
                                                    }
                                                >
                                                    <Button
                                                        type="text"
                                                        aria-label="Remove custom value"
                                                        data-testid={`${testId}Remove${field.name}`}
                                                        icon={
                                                            <MinusCircleOutlined />
                                                        }
                                                        onClick={() => {
                                                            remove(field.name);
                                                            setPendingKeyRevalidation(
                                                                (n) => n + 1
                                                            );
                                                        }}
                                                    />
                                                </Form.Item>
                                            )}
                                        </Col>
                                    </Row>
                                );
                            })}
                            <Form.Item>
                                <Button
                                    type="dashed"
                                    data-testid={`${testId}Add`}
                                    icon={<PlusOutlined />}
                                    disabled={
                                        fields.length >= MAX_CUSTOM_VALUES
                                    }
                                    onClick={() => {
                                        if (
                                            fields.length >= MAX_CUSTOM_VALUES
                                        ) {
                                            message.warning(
                                                `You may add up to ${MAX_CUSTOM_VALUES} custom values`
                                            );
                                            return;
                                        }
                                        add({ key: '', value: '' });
                                    }}
                                >
                                    Add custom value
                                </Button>
                            </Form.Item>
                        </>
                    )
                }
            </Form.List>
        </div>
    );
}
